import { useCallback } from "react";
import { CherryPayApi } from "../api/models";
import { useApiClient } from "./useApiClient";
import { useToast } from "./useToast";

interface UseNotificationCloneOptions {
  businessId: string;
  onPostCloneOperations: (
    notification: CherryPayApi.CherryPassNotificationSchedule
  ) => void;
}

export const useNotificationClone = (options: UseNotificationCloneOptions) => {
  const { businessId, onPostCloneOperations } = options;

  const apiClient = useApiClient();
  const { showInfoToast, showErrorToast, showSuccessToast } = useToast();

  return useCallback(
    async (notification: CherryPayApi.CherryPassNotificationSchedule) => {
      showInfoToast("Cloning notification...");

      if (!notification.Meta?.Id) {
        showErrorToast("Unable to clone notification: missing notification ID");
        return;
      }

      try {
        const cloneResult = await apiClient.cloneCherryPassNotificationSchedule(
          businessId,
          notification.Meta.Id
        );

        if (cloneResult.ok) {
          showSuccessToast("Notification cloned successfully.");

          // Create a new notification object with the cloned data
          const clonedNotification: CherryPayApi.CherryPassNotificationSchedule =
            {
              ...cloneResult.data,
              Meta: {
                Id: cloneResult.data.Meta?.Id || "",
              },
            };

          // Execute post-clone operations via callback
          onPostCloneOperations(clonedNotification);
        } else {
          showErrorToast(
            "Failed to clone notification: " + cloneResult.message
          );
        }
      } catch (error) {
        showErrorToast("Failed to clone notification");
        console.error("Clone notification error:", error);
      }
    },
    [
      businessId,
      apiClient,
      showInfoToast,
      showErrorToast,
      showSuccessToast,
      onPostCloneOperations,
    ]
  );
};
